// Data Pelanggan → Broadband
// Data pelanggan berasal dari Billing (Arta Billing). Tombol "Sync Billing" menarik
// data terbaru. Data teknis (OLT/PON/ONU/SN/ODP/VLAN/PPPoE/IP) diinput manual NOC dan
// dipertahankan saat sync ulang.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Search, Eye, Wifi, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { StatusBadge } from '@/components/StatusBadge';
import Breadcrumb from '@/components/Breadcrumb';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

const NA = <span className="text-muted-foreground italic">Tidak tersedia dari Billing</span>;

const pkgDisplay = (it) => it.package_name || (it.package_id ? `Paket tidak ditemukan (ID: ${it.package_id})` : '-');
const addrShort = (it) => (it.address ? String(it.address).split('\n')[0] : '-');

const TECH_FIELDS = [
  { key: 'tech_olt', label: 'OLT' },
  { key: 'tech_pon', label: 'PON' },
  { key: 'tech_onu', label: 'ONU / ONT' },
  { key: 'tech_onu_sn', label: 'Serial Number ONU' },
  { key: 'tech_odp', label: 'ODP' },
  { key: 'tech_vlan', label: 'VLAN' },
  { key: 'tech_pppoe_username', label: 'PPPoE Username' },
  { key: 'tech_ip_address', label: 'IP Address' },
];

export default function Broadband() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [billingReady, setBillingReady] = useState(null);

  const [viewing, setViewing] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [tech, setTech] = useState({});
  const [savingTech, setSavingTech] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 500, category: 'Broadband', sort_by: 'company_name', sort_dir: 'asc' };
      if (q) params.q = q;
      const { data } = await api.get('/customers', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/billing/status').then(({ data }) => setBillingReady(!!data.configured)).catch(() => setBillingReady(false));
  }, []);

  const syncBilling = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/billing/sync');
      toast.success(`Sync Billing selesai — ${data.created} baru, ${data.updated} diperbarui (dari ${data.total_from_billing} data Broadband)`);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSyncing(false); }
  };

  const openDetail = (row) => {
    setViewing(row);
    const t = {};
    TECH_FIELDS.forEach((f) => { t[f.key] = row[f.key] || ''; });
    // Auto-fill PPPoE username from Billing pppUser if available and not yet set manually.
    if (!t.tech_pppoe_username && row.ppp_user) t.tech_pppoe_username = row.ppp_user;
    setTech(t);
    setOpenView(true);
  };

  const saveTech = async () => {
    if (!viewing) return;
    setSavingTech(true);
    try {
      // Dedicated endpoint updates ONLY technical fields — never touches Billing data.
      await api.patch(`/billing/customers/${viewing.id}/tech`, tech);
      toast.success('Data teknis disimpan');
      setOpenView(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSavingTech(false); }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="space-y-4">
      <Breadcrumb items={[{ label: 'Data Pelanggan' }, { label: 'Broadband' }]} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Wifi className="w-6 h-6 text-primary" /> Pelanggan Broadband
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Data pelanggan bersumber dari Billing Artamedia. Data teknis diinput manual oleh NOC.</p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button size="sm" onClick={syncBilling} disabled={syncing} data-testid="broadband-sync-billing-button">
              <RefreshCw className={cn('w-4 h-4 mr-1.5', syncing && 'animate-spin')} />
              {syncing ? 'Menyinkronkan…' : 'Sync Billing'}
            </Button>
          )}
        </div>
      </div>

      {billingReady === false && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-3 text-sm text-amber-800 dark:text-amber-200" data-testid="broadband-billing-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Integrasi Billing belum dikonfigurasi. Isi <code>ACCESS_TOKEN</code> dan <code>REFRESH_TOKEN</code> (dari RIS) di <code>backend/.env</code> agar tombol Sync Billing berfungsi.</span>
        </div>
      )}

      <Card className="border-border">
        <CardContent className="p-4 space-y-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="broadband-search" placeholder="Cari nama, area, network id…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
          </div>

          <div className="border border-border rounded-md overflow-x-auto">
            <Table data-testid="broadband-table">
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="text-xs">CID</TableHead>
                  <TableHead className="text-xs">Nama Pelanggan</TableHead>
                  <TableHead className="text-xs">Paket Pelanggan</TableHead>
                  <TableHead className="text-xs">Alamat</TableHead>
                  <TableHead className="text-xs">Mitra</TableHead>
                  <TableHead className="text-xs">Tanggal Daftar</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
                {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">Belum ada data. Klik "Sync Billing" untuk menarik data pelanggan Broadband.</TableCell></TableRow>}
                {!loading && filtered.map((it) => (
                  <TableRow key={it.id} data-testid={`broadband-row-${it.id}`} className="hover:bg-accent/40">
                    <TableCell className="font-mono text-xs">{it.customer_id || it.billing_id || '-'}</TableCell>
                    <TableCell className="text-sm font-medium">{it.company_name || '-'}</TableCell>
                    <TableCell className="text-sm">{pkgDisplay(it)}</TableCell>
                    <TableCell className="text-sm max-w-[240px] truncate" title={it.address || ''}>{addrShort(it)}</TableCell>
                    <TableCell className="text-sm">{it.mitra_name || '-'}</TableCell>
                    <TableCell className="text-sm">{it.registration_date || '-'}</TableCell>
                    <TableCell><StatusBadge value={it.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openDetail(it)} data-testid={`broadband-view-${it.id}`}><Eye className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-muted-foreground">Menampilkan {filtered.length} dari {total} pelanggan Broadband</div>
        </CardContent>
      </Card>

      {/* Detail */}
      <Sheet open={openView} onOpenChange={setOpenView}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Detail Pelanggan Broadband</SheetTitle></SheetHeader>
          {viewing && (
            <div className="mt-4 space-y-5 text-sm">
              <section data-testid="broadband-detail-customer">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Informasi Pelanggan</h3>
                  <Badge variant="outline" className="text-[10px]">Sumber: Billing</Badge>
                </div>
                <Info k="CID / Customer ID" v={viewing.customer_id || viewing.billing_id || '-'} mono />
                <Info k="Nama" v={viewing.company_name || '-'} />
                <Info k="No. HP / WhatsApp" v={viewing.phone || NA} />
                <Info k="Email" v={viewing.email || NA} />
                <Info k="Alamat" v={viewing.address ? <span className="whitespace-pre-line">{viewing.address}</span> : NA} />
                <Info k="Area / Wilayah" v={viewing.area || NA} />
                <Info k="Kategori" v={viewing.customer_group || '-'} />
                <Info k="Paket / Produk" v={viewing.package_name || (viewing.package_id ? `Paket tidak ditemukan (ID: ${viewing.package_id})` : NA)} />
                <Info k="Mitra" v={viewing.mitra_name || '-'} />
                <Info k="Tanggal Daftar" v={viewing.registration_date || NA} />
                <Info k="Status" v={<StatusBadge value={viewing.status} />} />
              </section>

              <section className="pt-2 border-t border-border" data-testid="broadband-detail-technical">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Informasi Teknis (Input Manual NOC)</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {TECH_FIELDS.map((f) => (
                    <div key={f.key} className={cn('space-y-1.5', (f.key === 'tech_pppoe_username' || f.key === 'tech_ip_address' || f.key === 'tech_onu_sn') && 'col-span-2')}>
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{f.label}</Label>
                      <Input
                        value={tech[f.key] ?? ''}
                        disabled={!canWrite}
                        onChange={(e) => setTech({ ...tech, [f.key]: e.target.value })}
                        data-testid={`broadband-tech-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                {!canWrite && <p className="text-xs text-muted-foreground mt-2">Anda tidak memiliki hak untuk mengubah data teknis.</p>}
              </section>
            </div>
          )}
          {canWrite && (
            <SheetFooter className="mt-4">
              <Button variant="outline" onClick={() => setOpenView(false)} data-testid="broadband-detail-cancel">Tutup</Button>
              <Button onClick={saveTech} disabled={savingTech} data-testid="broadband-tech-save">{savingTech ? 'Menyimpan…' : 'Simpan Data Teknis'}</Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Info({ k, v, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-border last:border-0">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className={cn('col-span-2 text-foreground break-words', mono && 'font-mono text-xs')}>{v}</div>
    </div>
  );
}
